import { PSA_ID_KEY } from 'components/class/psa/psa-row/psa-actions';
import {
  Video,
  VideoAssignmentSubmission,
  VideoAssignmentSubmissionStatus,
} from 'types/VideoAssignmentSubmission';
export const SUBMISSION_BEING_PROCESSED_KEY = 'submission_being_processed';

export const formatDuration = (seconds?: number): string => {
  if (!seconds) return '0:00';

  const totalSeconds = Math.floor(seconds);

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = Math.floor(totalSeconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes < 10 ? '0' : ''}${minutes}:${
      remainingSeconds < 10 ? '0' : ''
    }${remainingSeconds}`;
  }

  return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
};

const getPSASessionNumber = (currentVideo: Video, allVideos: Video[] = []): number => {
  if (!allVideos.length) return 1;

  // Sort videos by creation date (oldest first)
  const sortedVideos = [...allVideos].sort(
    (a, b) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime()
  );

  // Find the position of the current video in the sorted array
  const videoIndex = sortedVideos.findIndex((video) => video.id === currentVideo.id);

  // Return the 1-based position (session number)
  return videoIndex >= 0 ? videoIndex + 1 : allVideos.length + 1;
};

const checkSubmissionStatus = (status: VideoAssignmentSubmissionStatus) => {
  return [
    VideoAssignmentSubmissionStatus.COMPLETED,
    VideoAssignmentSubmissionStatus.SUBMITTED,
    VideoAssignmentSubmissionStatus.FAILED,
  ].includes(status);
};

const getVideoPrefix = (coaching?: string) => {
  const isCoaching = coaching !== 'false';

  const BASE_URL = 'https://res.cloudinary.com/mindbodygreen/video/upload/c_scale,q_auto,w_1200/';
  const COACHING_SUBDIRECTORY = 'coaching-videos/';

  const videoUrlPrefix = isCoaching ? `${BASE_URL}${COACHING_SUBDIRECTORY}` : BASE_URL;

  return videoUrlPrefix;
};

const formatDateToLocaleString = (date: string) => {
  const dateObj = new Date(date);
  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
  };
  return dateObj.toLocaleString('en-US', options as Intl.DateTimeFormatOptions);
};

export const getPsaId = (): string => {
  const psaId = localStorage.getItem(PSA_ID_KEY);
  if (psaId) {
    return psaId;
  }
  return '';
};

const isCompletedOrSubmitted = (status: VideoAssignmentSubmissionStatus) => {
  return (
    status === VideoAssignmentSubmissionStatus.COMPLETED ||
    status === VideoAssignmentSubmissionStatus.SUBMITTED
  );
};

const VIDEO_THUMB_SIZES = [
  { screenSize: 768, transforms: 'c_fill,g_auto,q_auto,fl_lossy,w_143,h_100' },
  { transforms: 'c_fill,g_auto,q_auto,fl_lossy,w_85,h_60' },
];

const generateVideoThumbnail = (file: File) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const video = document.createElement('video');

    video.autoplay = true;
    video.muted = true;
    video.src = URL.createObjectURL(file);

    video.onloadeddata = () => {
      video.currentTime = 15;
    };

    video.onseeked = () => {
      const ctx = canvas.getContext('2d');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
      video.pause();
      return resolve(canvas.toDataURL('image/png'));
    };
  });
};

// Replace the previous compressImageForThumbnail function with this one
const compressImageForThumbnail = (base64Image: string): Promise<string> => {
  return new Promise((resolve) => {
    // If it's not a base64 image, return as is
    if (!base64Image || !base64Image.startsWith('data:image')) {
      resolve(base64Image);
      return;
    }

    try {
      // Log original size for debugging
      console.log(`Original thumbnail size: ${Math.round(base64Image.length / 1024)} KB`);

      // Create a temporary image
      const img = new Image();

      img.onload = () => {
        // Create a canvas with smaller dimensions
        const canvas = document.createElement('canvas');
        // Even smaller dimensions to ensure we're well under DynamoDB limits
        const MAX_WIDTH = 120;
        const MAX_HEIGHT = 72;

        // Scale proportionally
        let width = img.width;
        let height = img.height;

        if (width > MAX_WIDTH) {
          height = Math.round(height * (MAX_WIDTH / width));
          width = MAX_WIDTH;
        }

        if (height > MAX_HEIGHT) {
          width = Math.round(width * (MAX_HEIGHT / height));
          height = MAX_HEIGHT;
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and export with extremely low quality
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);

        // Convert to JPEG with very low quality (0.2 = 20%)
        const compressedImage = canvas.toDataURL('image/jpeg', 0.2);

        // Log compressed size for debugging
        console.log(`Compressed thumbnail size: ${Math.round(compressedImage.length / 1024)} KB`);

        resolve(compressedImage);
      };

      img.onerror = () => {
        console.error('Error loading image for compression');
        // If loading fails, return a placeholder
        resolve(
          'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='
        );
      };

      // Start loading the image
      img.src = base64Image;
    } catch (error) {
      console.error('Error compressing thumbnail:', error);
      // If compression fails, return a placeholder
      resolve(
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='
      );
    }
  });
};

// Utility function to ensure the filename has a valid video extension
const ensureVideoFileExtension = (filename: string): { fileName: string; contentType: string } => {
  // Extract any existing extension
  const parts = filename.split('.');
  const hasExtension = parts.length > 1;
  const existingExtension = hasExtension ? parts.pop()?.toLowerCase() : '';
  const baseName = parts.join('.');

  // Default to mp4 if no valid extension
  let fileExtension = 'mp4';
  let contentType = 'video/mp4';

  // Determine file extension and content type based on existing extension
  if (hasExtension) {
    switch (existingExtension) {
      case 'mp4':
        fileExtension = 'mp4';
        contentType = 'video/mp4';
        break;
      case 'mov':
        fileExtension = 'mov';
        contentType = 'video/quicktime';
        break;
      case 'avi':
        fileExtension = 'avi';
        contentType = 'video/x-msvideo';
        break;
      case 'webm':
        fileExtension = 'webm';
        contentType = 'video/webm';
        break;
      default:
        // If unknown extension, use mp4
        fileExtension = 'mp4';
        contentType = 'video/mp4';
    }
  }

  // Return the filename with guaranteed extension and the corresponding content type
  return {
    fileName: `${baseName}.${fileExtension}`,
    contentType: contentType,
  };
};

const saveSubmissionBeingProcessed = (submissionData: {
  assignmentId: string;
  isProcessing: boolean;
  videoId: string;
  submissionId: string;
  uploadTimestamp?: number;
}): void => {
  localStorage.setItem(SUBMISSION_BEING_PROCESSED_KEY, JSON.stringify(submissionData));
};

const getSubmissionBeingProcessed = () => {
  const storedSubmission = localStorage.getItem(SUBMISSION_BEING_PROCESSED_KEY);
  if (storedSubmission) {
    try {
      return JSON.parse(storedSubmission);
    } catch (error) {
      console.error('Error parsing submission data from localStorage:', error);
      return {
        assignmentId: '',
        isProcessing: false,
        videoId: '',
        submissionId: '',
        uploadTimestamp: 0,
      };
    }
  }
  return {
    assignmentId: '',
    isProcessing: false,
    videoId: '',
    submissionId: '',
    uploadTimestamp: 0,
  };
};

const clearSubmissionBeingProcessedFromStorage = (): void => {
  localStorage.removeItem(SUBMISSION_BEING_PROCESSED_KEY);
};

const isVideoProcessing = (video: Video): boolean => {
  if (!video || !video.clips || video.clips.length === 0) return true;

  const path = video.clips[0]?.path;
  return !path || path === '';
};

const isAnyVideoProcessing = (submission: VideoAssignmentSubmission): boolean => {
  if (!submission || !submission.videos) return false;
  return submission.videos.some((video) => isVideoProcessing(video));
};

// Students can withdraw a submission only within this window after submitting.
export const UNSUBMIT_WINDOW_HOURS = 2;
const UNSUBMIT_WINDOW_MS = UNSUBMIT_WINDOW_HOURS * 60 * 60 * 1000;

const isWithinUnsubmitWindow = (submittedDate?: string | null): boolean => {
  if (!submittedDate) return false;

  const submittedAt = new Date(submittedDate).getTime();
  return !Number.isNaN(submittedAt) && Date.now() - submittedAt < UNSUBMIT_WINDOW_MS;
};

export const videoAssignmentUtils = {
  formatDuration,
  checkSubmissionStatus,
  getVideoPrefix,
  getPSASessionNumber,
  formatDateToLocaleString,
  getPsaId,
  isCompletedOrSubmitted,
  VIDEO_THUMB_SIZES,
  compressImageForThumbnail,
  generateVideoThumbnail,
  ensureVideoFileExtension,
  saveSubmissionBeingProcessed,
  getSubmissionBeingProcessed,
  clearSubmissionBeingProcessedFromStorage,
  isVideoProcessing,
  isAnyVideoProcessing,
  isWithinUnsubmitWindow,
};
